import * as React from "react";
import { cn } from "../utils";

export interface IconProps extends React.SVGProps<SVGSVGElement> {
  size?: number;
}

// Official Quovy brand mark (q-mark variant). Single-path SVG; fills via currentColor.
export function Icon({ size = 40, className, ...props }: IconProps) {
  const height = Math.round(size * (637 / 549));

  return (
    <svg
      width={size}
      height={height}
      viewBox="0 0 549 637"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn("text-current", className)}
      aria-hidden="true"
      {...props}
    >
      <path
        d="M544.799 618.038C544.799 618.038 518.197 569.671 511.648 559.535C511.648 559.535 512.107 505.572 477.749 456.78C477.205 456.015 476.661 455.233 476.099 454.467C484.162 444.178 491.594 433.175 498.347 421.372C515.917 390.284 527.381 355.148 532.603 315.897C534.729 299.809 535.614 284.078 535.393 268.789C535.682 243.942 533.045 220.133 527.16 197.344H527.313C518.418 162.718 503.637 131.936 483.056 104.963C462.407 77.4806 436.893 54.9127 406.311 37.3448C375.882 19.2668 341.558 7.71923 303.406 2.65124C265.255 -2.41675 229.162 -0.222889 194.974 9.26683C161.381 18.2293 131.088 33.3823 104.043 54.5386C76.999 75.2697 54.7852 101.069 37.0787 132.157C19.5084 163.245 8.04427 198.381 2.82249 237.632C0.69636 253.721 -0.188084 269.452 0.0330336 284.741C-0.25612 309.588 2.38028 333.397 8.26541 356.186C17.1611 390.811 31.942 421.594 52.5229 448.566C73.1719 476.049 98.6855 498.617 129.268 516.185C159.697 534.263 194.021 545.81 232.172 550.878C270.324 555.946 306.417 553.752 340.605 544.263C349.059 541.984 357.291 539.331 365.302 536.32C361.118 531.916 356.628 526.474 352.001 519.637C349.212 515.589 344.143 508.617 338.615 501.27C328.92 506.559 318.987 510.538 309.07 512.443C237.394 526.406 190.347 493.702 163.014 468.566C135.085 443.277 114.64 410.131 101.781 369.043C94.0591 344.859 89.9429 318.703 89.2795 290.693C89.2795 290.693 89.4326 277.173 89.5006 270.268C89.5687 267.768 89.6537 265.268 89.7898 262.768H89.6367C90.0109 249.384 91.0314 235.643 92.8684 221.459C98.4474 179.64 110.66 144.283 129.557 115.542C148.454 86.8002 188 48.5182 226.594 41.0183C298.27 27.0558 345.317 59.7596 372.65 84.8955C400.579 110.184 421.024 143.33 433.883 184.419C441.605 208.602 445.721 234.758 446.384 262.768C446.384 262.768 446.231 276.289 446.163 283.193C446.095 285.693 446.01 288.193 445.874 290.693H446.027C445.653 304.077 444.633 317.819 442.796 332.002C439.547 356.169 434.07 378.142 426.382 397.954C373.841 348.839 298.44 307.003 217.188 336.985C217.188 336.985 208.003 340.812 218.803 341.832C229.604 342.785 255.917 335.149 280.478 363.958C305.039 392.767 349.127 505.59 386.768 535.725C414.918 558.293 468.819 563.837 490.659 570.096C483.209 559.773 453.732 515.062 420.769 473.175C391.564 436.066 358.499 401.22 352.29 393.414C347.97 387.988 334.737 375.012 337.374 373.312C342.17 370.216 387.686 420.607 426.042 468.753C463.462 515.725 496.391 567.086 496.391 567.086L533.641 623.038L549 637L544.816 618.038H544.799Z"
        fill="currentColor"
      />
    </svg>
  );
}

export interface LogotypeProps extends React.SVGProps<SVGSVGElement> {
  height?: number;
}

// Official Quovy logotype lockup — the full "Quovy" wordmark whose leading "Q"
// is the brand q-mark. Mirrors apps/web/public/brand/logomark.svg, inlined here
// so it fills via currentColor and adapts to light/dark themes (the raw asset is
// fixed black). Height-driven since it's a horizontal lockup; width follows the
// 2232:791 aspect ratio.
export function Logotype({ height = 32, className, ...props }: LogotypeProps) {
  const width = Math.round(height * (2232 / 791));

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 2232 791"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn("text-current", className)}
      role="img"
      aria-label="Quovy"
      {...props}
    >
      <path
        d="M572.584 650.055C572.584 650.055 544.626 599.183 537.743 588.521C537.743 588.521 538.226 531.764 502.115 480.444C501.543 479.639 500.971 478.816 500.381 478.011C508.855 467.189 516.667 455.616 523.764 443.202C542.23 410.503 554.279 373.547 559.767 332.262C562.002 315.34 562.931 298.794 562.699 282.713C563.003 256.579 560.232 231.537 554.046 207.567H554.207C544.858 171.148 529.323 138.771 507.693 110.401C485.991 81.4945 459.176 57.7575 427.034 39.2795C395.053 20.2649 358.978 8.11912 318.881 2.78859C278.784 -2.54195 240.85 -0.234436 204.918 9.7469C169.612 19.1737 137.773 35.1117 109.35 57.364C80.9261 79.1691 57.5793 106.305 38.9698 139.003C20.5034 171.702 8.45454 208.658 2.96644 249.943C0.731875 266.865 -0.197677 283.411 0.0347183 299.492C-0.269183 325.626 2.50167 350.669 8.68696 374.638C18.0364 411.058 33.5711 443.434 55.2017 471.804C76.9038 500.711 103.719 524.448 135.861 542.926C167.842 561.94 203.917 574.086 244.014 579.417C284.111 584.747 322.045 582.44 357.977 572.458C366.861 570.061 375.514 567.271 383.933 564.105C379.536 559.472 374.816 553.748 369.954 546.557C367.022 542.3 361.695 534.966 355.885 527.238C345.695 532.801 335.256 536.987 324.834 538.99C249.502 553.676 200.055 519.278 171.328 492.84C141.974 466.241 120.487 431.378 106.972 388.161C98.8562 362.725 94.5301 335.214 93.8329 305.753C93.8329 305.753 93.9938 291.532 94.0653 284.27C94.1368 281.64 94.2262 279.011 94.3692 276.381H94.2083C94.6016 262.303 95.6742 247.85 97.6048 232.932C103.468 188.946 116.304 151.757 136.165 121.527C156.025 91.297 197.588 51.0317 238.15 43.1433C313.482 28.4574 362.928 62.8555 391.656 89.2935C421.009 115.893 442.497 150.756 456.012 193.972C464.128 219.409 468.454 246.92 469.151 276.381C469.151 276.381 468.99 290.602 468.918 297.864C468.847 300.494 468.758 303.123 468.615 305.753H468.775C468.382 319.83 467.31 334.284 465.379 349.202C461.964 374.62 456.208 397.731 448.128 418.57C392.907 366.911 313.661 322.907 228.264 354.443C228.264 354.443 218.611 358.468 229.963 359.541C241.314 360.543 268.969 352.511 294.783 382.813C320.597 413.115 366.933 531.782 406.494 563.479C436.079 587.216 492.73 593.047 515.683 599.63C507.854 588.772 476.874 541.745 442.229 497.688C411.535 458.657 376.783 422.005 370.258 413.794C365.717 408.088 351.809 394.44 354.58 392.651C359.621 389.396 407.459 442.397 447.771 493.037C487.099 542.443 521.708 596.464 521.708 596.464L560.857 655.314L577 670L572.602 650.055H572.584Z"
        fill="currentColor"
      />
      <path
        d="M1865.02 790.909C1850.82 790.909 1841.86 788.725 1838.15 784.465C1835.09 780.752 1833.56 773.98 1833.56 764.15V717.84L1837.27 714.127H1879.88C1895.29 714.127 1907.09 712.598 1915.06 709.54C1923.04 707.028 1930.47 699.928 1937.24 688.242C1944.01 677.101 1953.63 658.643 1965.97 632.757L1998.42 560.562L1828.97 183.969L1766.92 166.166V151.312H1966.41V166.166L1912.33 183.969V199.478L2035.46 467.943H2038.19L2141.87 205.048V188.556L2094.23 166.166V151.312H2232V166.166L2184.37 186.699L2001.92 641.058C1996.34 655.257 1991.43 666.943 1987.06 676.227C1983.34 686.057 1979.08 695.341 1974.06 703.969C1969.14 713.253 1962.04 724.284 1952.75 737.282C1940.41 755.194 1926.53 768.41 1911.13 777.038C1895.72 786.322 1880.32 790.909 1864.81 790.909H1865.02Z"
        fill="currentColor"
      />
      <path
        d="M1614.18 567.88L1444.73 183.969L1393.6 166.166V151.312H1582.17V166.166L1528.09 182.986V199.478L1646.63 457.785H1650.34L1755.01 205.048V188.556L1699.29 166.166V151.312H1869.83V166.166L1801.33 187.682L1633.74 567.989H1614.18V567.88Z"
        fill="currentColor"
      />
      <path
        d="M1226.78 577.164C1186.03 577.164 1150.19 567.553 1119.38 548.439C1089.12 529.326 1065.41 503.44 1048.04 470.674C1031.33 438.017 1023.02 400.991 1023.02 359.596C1023.02 318.202 1031.33 281.176 1048.04 248.519C1065.3 215.862 1089.12 189.867 1119.38 170.753C1150.19 151.64 1186.03 142.028 1226.78 142.028C1267.53 142.028 1303.03 151.64 1333.3 170.753C1364.11 189.867 1387.92 215.752 1404.64 248.519C1421.9 281.176 1430.53 318.202 1430.53 359.596C1430.53 400.991 1421.9 438.017 1404.64 470.674C1387.92 503.331 1364.22 529.326 1333.3 548.439C1303.03 567.553 1267.53 577.164 1226.78 577.164ZM1239.67 537.299C1264.36 537.299 1284.68 529.544 1300.74 514.144C1316.8 498.744 1328.49 478.975 1335.92 454.946C1343.35 430.262 1347.06 404.705 1347.06 378.164C1347.06 345.507 1342.47 313.942 1333.19 283.797C1324.56 253.543 1310.35 228.859 1290.58 209.745C1271.46 190.632 1246.44 181.02 1215.63 181.02C1190.29 181.02 1169.64 188.775 1153.58 204.175C1137.52 219.575 1125.5 239.344 1117.42 263.373C1109.99 287.401 1106.27 313.068 1106.27 340.155C1106.27 372.812 1110.86 404.377 1120.15 434.522C1129.43 464.776 1143.96 489.46 1163.63 508.574C1183.4 527.687 1208.64 537.299 1239.56 537.299H1239.67Z"
        fill="currentColor"
      />
      <path
        d="M736.565 577.164C700.184 577.164 670.249 568.208 646.76 550.295C623.926 531.837 612.455 504.641 612.455 468.817V183.859L575.965 166.056V151.202H686.527V425.237C686.527 452.433 694.831 473.076 711.546 487.275C728.808 501.474 751.095 508.573 778.19 508.573C794.905 508.573 813.041 505.842 832.815 500.272C853.136 494.702 869.196 486.729 880.995 476.244V183.968L837.076 166.165V151.311H955.068V537.08L998.987 553.026V567.88H880.995V509.556H876.407C852.918 533.038 830.084 550.295 807.906 561.436C786.274 571.921 762.567 577.164 736.565 577.164Z"
        fill="currentColor"
      />
    </svg>
  );
}

export interface WordmarkProps extends React.HTMLAttributes<HTMLSpanElement> {
  size?: "sm" | "md" | "lg";
}

export function Wordmark({ size = "md", className, ...props }: WordmarkProps) {
  const sizeClasses = {
    sm: "text-xs tracking-[0.2em]",
    md: "text-sm tracking-[0.3em]",
    lg: "text-base tracking-[0.4em]",
  };

  return (
    <span
      className={cn(
        "font-semibold uppercase font-[var(--font-inter,sans-serif)]",
        sizeClasses[size],
        className,
      )}
      {...props}
    >
      Quovy
    </span>
  );
}

export interface LogoProps extends React.HTMLAttributes<HTMLDivElement> {
  iconSize?: number;
  wordmarkSize?: "sm" | "md" | "lg";
  iconClassName?: string;
  wordmarkClassName?: string;
}

export function Logo({
  iconSize = 40,
  wordmarkSize = "md",
  iconClassName,
  wordmarkClassName,
  className,
  ...props
}: LogoProps) {
  return (
    <div className={cn("flex items-center gap-3", className)} {...props}>
      <Icon size={iconSize} className={iconClassName} />
      <Wordmark size={wordmarkSize} className={wordmarkClassName} />
    </div>
  );
}

// Compact brand mark in a square foreground tile. Canonical surface for
// app-shell affordances (sidebar, sign-in header, invite header, etc.).
export interface BrandMarkProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: "sm" | "md" | "lg";
  variant?: "foreground" | "sidebar";
}

export function BrandMark({
  size = "md",
  variant = "foreground",
  className,
  ...props
}: BrandMarkProps) {
  const tileSizes = {
    sm: { tile: "size-6", icon: 14 },
    md: { tile: "size-8", icon: 18 },
    lg: { tile: "size-10", icon: 22 },
  };
  const { tile, icon } = tileSizes[size];

  const palette =
    variant === "sidebar"
      ? "bg-sidebar-primary text-sidebar-primary-foreground"
      : "bg-foreground text-background";

  return (
    <div
      className={cn("flex items-center justify-center rounded-md", tile, palette, className)}
      {...props}
    >
      <Icon size={icon} />
    </div>
  );
}
